import type { UIMessage } from 'ai'
import type { QueryClient } from '@tanstack/react-query'
import {
  type GetLlmChatSessionResponse,
  llmChatRowsToUiMessages,
  patchLlmChatSessionInListCache,
} from '@/shared/hooks/api/llmChat'
import {
  setProductRecommendationsCache,
  type ProductRecommendation,
} from '@/shared/hooks/api/product'
import { useLlmChatStore } from './store/llmChatStore'

export type ConsumeLlmChatUiMessageStreamInput = {
  url: string
  body: Record<string, unknown>
  sessionId: string
  queryClient: QueryClient
  setMessages: (
    updater: UIMessage[] | ((previous: UIMessage[]) => UIMessage[])
  ) => void
  signal?: AbortSignal
}

function parseSseDataLine(line: string): unknown | null {
  const trimmed = line.trim()
  if (!trimmed.startsWith('data:')) {
    return null
  }
  const payload = trimmed.slice('data:'.length).trim()
  if (payload === '[DONE]') {
    return { type: '__done__' }
  }
  try {
    return JSON.parse(payload) as unknown
  } catch {
    return null
  }
}

/**
 * Consumes Lex LLM chat UI message SSE (same wire format as {@link DefaultChatTransport}).
 */
export async function consumeLlmChatUiMessageStream(
  input: ConsumeLlmChatUiMessageStreamInput
): Promise<void> {
  const response = await fetch(input.url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify(input.body),
    signal: input.signal,
  })

  if (!response.ok) {
    throw new Error(`Chat stream failed (${response.status})`)
  }
  if (!response.body) {
    throw new Error('Chat stream returned no body')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let assistantMessageId: string = crypto.randomUUID()
  let assistantText = ''

  const upsertStreamingAssistant = () => {
    input.setMessages((previous) => {
      const existingIndex = previous.findIndex(
        (message) => message.id === assistantMessageId
      )
      const assistantMessage: UIMessage = {
        id: assistantMessageId,
        role: 'assistant',
        parts: [{ type: 'text', text: assistantText }],
      }
      if (existingIndex === -1) {
        return [...previous, assistantMessage]
      }
      const next = [...previous]
      next[existingIndex] = assistantMessage
      return next
    })
  }

  while (true) {
    const { done, value } = await reader.read()
    if (done) {
      break
    }
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() ?? ''

    for (const line of lines) {
      const parsed = parseSseDataLine(line)
      if (parsed === null || typeof parsed !== 'object') {
        continue
      }
      const event = parsed as Record<string, unknown>
      if (event.type === '__done__') {
        continue
      }
      if (event.type === 'start' && typeof event.messageId === 'string') {
        assistantMessageId = event.messageId
        assistantText = ''
        continue
      }
      if (event.type === 'text-delta' && typeof event.delta === 'string') {
        assistantText += event.delta
        upsertStreamingAssistant()
        continue
      }
      if (event.type === 'data-lex-llm-chat-session-sync') {
        const data = event.data as Pick<
          GetLlmChatSessionResponse,
          'session' | 'messages'
        >
        if (
          data.session !== null &&
          data.session !== undefined &&
          data.messages !== undefined
        ) {
          useLlmChatStore.getState().setSessionFromSync(input.sessionId, data)
          patchLlmChatSessionInListCache(input.queryClient, data.session)
          input.setMessages(llmChatRowsToUiMessages(data.messages))
        }
        continue
      }
      if (event.type === 'data-lex-product-recommendations-sync') {
        const data = event.data as {
          productId: string
          sessionId: string
          recommendations: ProductRecommendation[]
        }
        useLlmChatStore.getState().setProductRecommendations(
          data.productId,
          data.sessionId,
          data.recommendations
        )
        setProductRecommendationsCache(
          input.queryClient,
          data.productId,
          data.sessionId,
          data.recommendations
        )
        continue
      }
      if (event.type === 'data-lex-llm-chat-recommendation-gate-pending') {
        useLlmChatStore.getState().setRecommendationGatePending(
          input.sessionId,
          true
        )
        continue
      }
      if (event.type === 'data-lex-llm-chat-stream-error') {
        const errorData = event.data as {
          message: string
          retryable: boolean
          userMessageId: string | null
        }
        useLlmChatStore.getState().handleStreamError(input.sessionId, errorData)
        continue
      }
      if (event.type === 'error' && typeof event.errorText === 'string') {
        throw new Error(event.errorText)
      }
    }
  }
}

export async function continueAfterCatalogSaveStream(input: {
  sessionId: string
  recommendationId: string
  model?: string
  queryClient: QueryClient
  setMessages: ConsumeLlmChatUiMessageStreamInput['setMessages']
  signal?: AbortSignal
}): Promise<void> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL ?? ''
  await consumeLlmChatUiMessageStream({
    url: `${baseUrl}/v1/llm-chat/sessions/${input.sessionId}/continue-after-catalog-save`,
    body: {
      recommendationId: input.recommendationId,
      ...(input.model !== undefined && input.model !== ''
        ? { model: input.model }
        : {}),
    },
    sessionId: input.sessionId,
    queryClient: input.queryClient,
    setMessages: input.setMessages,
    signal: input.signal,
  })
}

export async function continueAfterCatalogDismissStream(input: {
  sessionId: string
  recommendationId: string
  model?: string
  queryClient: QueryClient
  setMessages: ConsumeLlmChatUiMessageStreamInput['setMessages']
  signal?: AbortSignal
}): Promise<void> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL ?? ''
  await consumeLlmChatUiMessageStream({
    url: `${baseUrl}/v1/llm-chat/sessions/${input.sessionId}/continue-after-catalog-dismiss`,
    body: {
      recommendationId: input.recommendationId,
      ...(input.model !== undefined && input.model !== ''
        ? { model: input.model }
        : {}),
    },
    sessionId: input.sessionId,
    queryClient: input.queryClient,
    setMessages: input.setMessages,
    signal: input.signal,
  })
}
